import { create } from 'zustand';
import { getAllParameterNamesGrpc } from '../api/grpc/pcfMappingClient';
import { normalizeMappingProjectKey } from '../lib/mappingProjectIdentity';
import { registerRevitModelCacheInvalidator } from '../lib/revit/modelCacheInvalidation';
import { RevitCatalogInspectionTimeoutMs } from '../lib/revit/revitOperationTimeoutPolicy';

export interface SampledParameterNamesFetchContext {
  projectKey?: string | null;
  modelVersion?: number | null;
  force?: boolean;
}

interface SampledParameterNamesState {
  names: string[];
  isLoading: boolean;
  isLoaded: boolean;
  attempted: boolean;
  error: string | null;
  projectKey: string | null;
  modelVersion: number | null;
  fetchNames: (context?: SampledParameterNamesFetchContext) => Promise<string[]>;
}

interface SampledParameterNamesScope {
  projectKey: string | null;
  modelVersion: number | null;
}

let fetchSequence = 0;
let inFlightRequest: (SampledParameterNamesScope & { promise: Promise<string[]> }) | null = null;

function normalizeModelVersion(modelVersion: number | null | undefined): number | null {
  return typeof modelVersion === 'number' && Number.isFinite(modelVersion)
    ? modelVersion
    : null;
}

function isSameScope(
  scope: SampledParameterNamesScope,
  projectKey: string | null,
  modelVersion: number | null
): boolean {
  return scope.projectKey === projectKey && scope.modelVersion === modelVersion;
}

function withSampledCatalogTimeout<T>(promise: Promise<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timeoutId = globalThis.setTimeout(() => {
      reject(new Error('Timed out while sampling active Revit project parameters.'));
    }, RevitCatalogInspectionTimeoutMs);

    promise
      .then((value) => {
        globalThis.clearTimeout(timeoutId);
        resolve(value);
      })
      .catch((error) => {
        globalThis.clearTimeout(timeoutId);
        reject(error);
      });
  });
}

/**
 * Store for the sampled active-project parameter name catalog. Every mapping
 * consumer (profile gate matching, readiness diff, repair verification, load
 * confirmation) needs the same Revit enumeration; caching it per project scope
 * with in-flight joining keeps the page from paying one full enumeration per
 * consumer per visit.
 */
export const useSampledParameterNamesStore = create<SampledParameterNamesState>((set, get) => ({
  names: [],
  isLoading: false,
  isLoaded: false,
  attempted: false,
  error: null,
  projectKey: null,
  modelVersion: null,

  fetchNames: async (context = {}) => {
    const projectKey = normalizeMappingProjectKey(context.projectKey);
    const modelVersion = normalizeModelVersion(context.modelVersion);
    const state = get();
    const sameScope = isSameScope(state, projectKey, modelVersion);

    if (!context.force && sameScope) {
      if (state.isLoaded) {
        return state.names;
      }

      if (state.isLoading && inFlightRequest && isSameScope(inFlightRequest, projectKey, modelVersion)) {
        return inFlightRequest.promise;
      }
    }

    const fetchId = ++fetchSequence;
    set({
      names: sameScope ? state.names : [],
      isLoading: true,
      isLoaded: false,
      attempted: sameScope ? state.attempted : false,
      error: null,
      projectKey,
      modelVersion,
    });

    const request = (async (): Promise<string[]> => {
      const result = await withSampledCatalogTimeout(getAllParameterNamesGrpc());
      if (!result.success) {
        throw new Error(result.error || 'Failed to sample active Revit project parameters.');
      }

      return [...result.parameterNames];
    })();
    inFlightRequest = { projectKey, modelVersion, promise: request };

    try {
      const names = await request;
      if (fetchId === fetchSequence) {
        set({
          names,
          isLoading: false,
          isLoaded: true,
          attempted: true,
          error: null,
          projectKey,
          modelVersion,
        });
      }
      return names;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to sample active Revit project parameters.';
      if (fetchId === fetchSequence) {
        set({
          names: [],
          isLoading: false,
          isLoaded: false,
          attempted: true,
          error: message,
          projectKey,
          modelVersion,
        });
      }
      throw error instanceof Error ? error : new Error(message);
    } finally {
      if (inFlightRequest?.promise === request) {
        inFlightRequest = null;
      }
    }
  },
}));

export function invalidateSampledParameterNamesStore(): void {
  fetchSequence += 1;
  inFlightRequest = null;
  useSampledParameterNamesStore.setState({
    names: [],
    isLoading: false,
    isLoaded: false,
    attempted: false,
    error: null,
    projectKey: null,
    modelVersion: null,
  });
}

registerRevitModelCacheInvalidator((event) => {
  if (
    event.eventType === 'ModelChanged' ||
    event.eventType === 'DocumentOpened' ||
    event.eventType === 'DocumentChanged' ||
    event.eventType === 'DocumentClosed'
  ) {
    invalidateSampledParameterNamesStore();
  }
});
